/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "/api";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fieldClass = "h-10 min-w-0 w-full border border-gray-200 bg-white px-2 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500";
const disabledFieldClass = `${fieldClass} bg-gray-50 text-gray-500`;
const tabClass = "h-12 px-6 rounded-md text-sm font-semibold transition-colors";
const sensorGridClass = "grid gap-2 items-center w-full";
const relayGridClass = "grid gap-2 items-center w-full";
const sensorGridStyle = {
  gridTemplateColumns: "minmax(0,0.7fr) minmax(0,1.55fr) minmax(0,0.6fr) minmax(0,0.6fr) minmax(0,0.95fr) minmax(0,1.25fr) minmax(0,0.55fr) minmax(0,0.55fr) 36px",
};
const relayGridStyle = {
  gridTemplateColumns: "minmax(0,0.62fr) minmax(0,1.35fr) minmax(0,0.82fr) minmax(0,0.78fr) minmax(0,0.46fr) minmax(0,0.72fr) minmax(0,0.82fr) minmax(0,0.82fr) minmax(0,0.62fr) minmax(0,0.62fr) 36px",
};

const formulaGuide = [
  { title: "Values", items: [["x", "Current sensor value after its factor"], ["d1, i1, ts", "Configured sensor, relay, or timestamp values"], ["data.d1", "Source telemetry alias for d1"]] },
  { title: "Conditions", items: [["a > b ? x : y", "If a is greater than b, use x; otherwise y"], ["d1<100?100:d1<200?200:300", "Multi-step grade"], ["== != < <= > >= && || !", "Comparisons and logic"]] },
  { title: "Math", items: [["round(x, d)", "Round to d decimal places"], ["floor(x), ceil(x), abs(x)", "Integer and absolute value helpers"], ["sqrt(x), pow(x,e), log(x), log10(x)", "Roots, powers, and logarithms"], ["min(...), max(...), avg(a,b,...)", "Minimum, maximum, and average"]] },
  { title: "Helpers", items: [["clamp(v, lo, hi)", "Keep v within a range"], ["scale(v, inLo, inHi, outLo, outHi)", "Map one range to another"], ["pct(part, total)", "Percentage"], ["ifNull(v, fallback)", "Fallback for an empty value"]] },
];

const emptyRelayTiming = (
  <>
    <input placeholder="From" disabled className={disabledFieldClass} />
    <input placeholder="To" disabled className={disabledFieldClass} />
    <input placeholder="On" disabled className={disabledFieldClass} />
    <input placeholder="Off" disabled className={disabledFieldClass} />
  </>
);

const emptyRelayTrigger = (
  <>
    <input placeholder="Sensor" disabled className={disabledFieldClass} />
    <input placeholder="Cond" disabled className={disabledFieldClass} />
    <input placeholder="Value" disabled className={disabledFieldClass} />
  </>
);

export default function ProductDetails() {
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const [editingSensors, setEditingSensors] = useState({});
  const [editingRelays, setEditingRelays] = useState({});
  const [tab, setTab] = useState("sensors");
  const [sensors, setSensors] = useState([]);
  const [relays, setRelays] = useState([]);
  const [product, setProduct] = useState(null);
  const [ruleBook, setRuleBook] = useState("");
  const [savingRuleBook, setSavingRuleBook] = useState(false);
  const [error, setError] = useState("");

  const [newSensor, setNewSensor] = useState({
    key: "",
    label: "",
    factor: "",
    unit: "",
    formula: "",
    min_value: "",
    max_value: "",
    group_type: "",
  });

  const defaultRelay = {
    key: "",
    label: "",
    mode: "manual",
    default_state: false,
    trigger_sensor: "",
    trigger_condition: ">",
    trigger_value: "",
    timer_on: "",
    timer_off: "",
    schedule_start_day: 1,
    schedule_end_day: 5,
    schedule_start_time: "09:00",
    schedule_end_time: "17:00",
  };

  const [newRelay, setNewRelay] = useState(defaultRelay);

  const apiFetch = async (url, options) => {
    const res = await fetch(url, options);
    if (!res.ok) {
      let message = `Request failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.message) message = body.message;
      } catch {
        // response body wasn't JSON — keep the generic message
      }
      throw new Error(message);
    }
    return res;
  };

  const fetchAll = async () => {
    try {
      const [sRes, rRes, pRes] = await Promise.all([
        apiFetch(`${API}/products/${id}/sensors`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        apiFetch(`${API}/products/${id}/relays`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        apiFetch(`${API}/products/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const sData = await sRes.json();
      const rData = await rRes.json();
      const pData = await pRes.json();

      setSensors(Array.isArray(sData) ? sData : []);
      setRelays(Array.isArray(rData) ? rData : []);
      setProduct(pData);
      setRuleBook(pData.rule_book || "");
    } catch (err) {
      console.error("Fetch error:", err);
      setError(`Failed to load product configuration: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [id]);

  const saveSensor = async () => {
    if (!newSensor.key || !newSensor.label) return;

    try {
      await apiFetch(`${API}/products/${id}/sensors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newSensor,
          factor: Number(newSensor.factor) || 1,
          min_value: newSensor.min_value || null,
          max_value: newSensor.max_value || null,
          group_type: newSensor.group_type || null,
        }),
      });

      setError("");
      setNewSensor({
        key: "",
        label: "",
        factor: "",
        unit: "",
        formula: "",
        min_value: "",
        max_value: "",
        group_type: "",
      });

      fetchAll();
    } catch (err) {
      setError(`Failed to add sensor "${newSensor.key}": ${err.message}`);
    }
  };

  const saveRelay = async () => {
    if (!newRelay.key || !newRelay.label) return;

    try {
      await apiFetch(`${API}/products/${id}/relays`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newRelay,
          trigger_value: newRelay.trigger_value || null,
        }),
      });

      setError("");
      setNewRelay(defaultRelay);
      fetchAll();
    } catch (err) {
      setError(`Failed to add relay "${newRelay.key}": ${err.message}`);
    }
  };

  // Clears just the fields that were just committed, so any other
  // field the user is still mid-edit on (not yet blurred) survives fetchAll().
  const clearDraftFields = (setDrafts, id, fields) => {
    setDrafts((prev) => {
      if (!prev[id]) return prev;
      const remaining = { ...prev[id] };
      fields.forEach((f) => delete remaining[f]);
      const next = { ...prev };
      if (Object.keys(remaining).length) next[id] = remaining;
      else delete next[id];
      return next;
    });
  };

  const updateSensor = async (sensor, changedFields = []) => {
    try {
      await apiFetch(`${API}/products/sensors/${sensor.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...sensor,
          factor: Number(sensor.factor) || 1,
          group_type: sensor.group_type || null,
        }),
      });

      setError("");
      clearDraftFields(setEditingSensors, sensor.id, changedFields);
      fetchAll();
    } catch (err) {
      setError(`Failed to save sensor "${sensor.key}": ${err.message}`);
    }
  };

  const updateRelay = async (relay, changedFields = []) => {
    try {
      await apiFetch(`${API}/products/relays/${relay.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(relay),
      });

      setError("");
      clearDraftFields(setEditingRelays, relay.id, changedFields);
      fetchAll();
    } catch (err) {
      setError(`Failed to save relay "${relay.key}": ${err.message}`);
    }
  };

  const deleteSensor = async (sensorId) => {
    try {
      await apiFetch(`${API}/products/sensors/${sensorId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setError("");
      fetchAll();
    } catch (err) {
      setError(`Failed to delete sensor: ${err.message}`);
    }
  };

  const deleteRelay = async (relayId) => {
    try {
      await apiFetch(`${API}/products/relays/${relayId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setError("");
      fetchAll();
    } catch (err) {
      setError(`Failed to delete relay: ${err.message}`);
    }
  };

  const saveRuleBook = async () => {
    try {
      setSavingRuleBook(true);
      await apiFetch(`${API}/products/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rule_book: ruleBook }),
      });
      setError("");
      setProduct((current) => current ? { ...current, rule_book: ruleBook } : current);
    } catch (err) {
      setError(`Failed to save rule book: ${err.message}`);
    } finally {
      setSavingRuleBook(false);
    }
  };

  // Sensor/relay text fields commit on blur (not on every keystroke) so
  // rapid typing can't fire overlapping PUTs that resolve out of order.
  const sensorField = (sensor, field, fallback = "") =>
    editingSensors[sensor.id]?.[field] ?? sensor[field] ?? fallback;

  const setSensorField = (sensorId, field, value) => {
    setEditingSensors((prev) => ({
      ...prev,
      [sensorId]: { ...prev[sensorId], [field]: value },
    }));
  };

  const commitSensorField = (sensor, field) => {
    const draft = editingSensors[sensor.id];
    if (!draft || draft[field] === undefined) return;
    updateSensor({ ...sensor, [field]: draft[field] }, [field]);
  };

  const relayField = (relay, field, fallback = "") =>
    editingRelays[relay.id]?.[field] ?? relay[field] ?? fallback;

  const setRelayField = (relayId, field, value) => {
    setEditingRelays((prev) => ({
      ...prev,
      [relayId]: { ...prev[relayId], [field]: value },
    }));
  };

  const commitRelayField = (relay, field, valueOverride) => {
    const value = valueOverride !== undefined ? valueOverride : editingRelays[relay.id]?.[field];
    if (value === undefined) return;
    updateRelay({ ...relay, [field]: value }, [field]);
  };

  // onFieldBlur is only passed for existing relay rows (network-backed);
  // the "new relay" form passes none since it's local state until Add is clicked.
  const renderTriggerFields = (relay, onChange, onFieldBlur) => (
    <>
      <input
        placeholder="Sensor"
        value={relay.trigger_sensor || ""}
        onChange={(event) => onChange({ trigger_sensor: event.target.value })}
        onBlur={onFieldBlur ? () => onFieldBlur("trigger_sensor") : undefined}
        className={fieldClass}
      />
      <select
        value={relay.trigger_condition || ">"}
        onChange={(event) => {
          onChange({ trigger_condition: event.target.value });
          onFieldBlur?.("trigger_condition", event.target.value);
        }}
        className={fieldClass}
      >
        <option value=">">&gt;</option>
        <option value="<">&lt;</option>
        <option value=">=">&ge;</option>
        <option value="<=">&le;</option>
      </select>
      <input
        placeholder="Value"
        value={relay.trigger_value || ""}
        onChange={(event) => onChange({ trigger_value: event.target.value })}
        onBlur={onFieldBlur ? () => onFieldBlur("trigger_value") : undefined}
        className={fieldClass}
      />
    </>
  );

  const renderScheduleFields = (relay, onChange, onFieldBlur) => (
    <>
      <select
        value={relay.schedule_start_day ?? 1}
        onChange={(event) => {
          const value = Number(event.target.value);
          onChange({ schedule_start_day: value });
          onFieldBlur?.("schedule_start_day", value);
        }}
        className={fieldClass}
      >
        {DAYS.map((day, index) => <option key={day} value={index}>From {day}</option>)}
      </select>
      <select
        value={relay.schedule_end_day ?? 5}
        onChange={(event) => {
          const value = Number(event.target.value);
          onChange({ schedule_end_day: value });
          onFieldBlur?.("schedule_end_day", value);
        }}
        className={fieldClass}
      >
        {DAYS.map((day, index) => <option key={day} value={index}>To {day}</option>)}
      </select>
      <input
        type="time"
        value={relay.schedule_start_time || relay.timer_on || ""}
        onChange={(event) => onChange({ schedule_start_time: event.target.value, timer_on: event.target.value })}
        onBlur={onFieldBlur ? () => onFieldBlur("schedule_start_time") : undefined}
        className={fieldClass}
      />
      <input
        type="time"
        value={relay.schedule_end_time || relay.timer_off || ""}
        onChange={(event) => onChange({ schedule_end_time: event.target.value, timer_off: event.target.value })}
        onBlur={onFieldBlur ? () => onFieldBlur("schedule_end_time") : undefined}
        className={fieldClass}
      />
    </>
  );

  const relayDraft = (relay) => ({ ...relay, ...editingRelays[relay.id] });

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-1">{product?.name || "Product"} Configuration</h1>
      <p className="mb-6 text-sm text-slate-500">Use sensor keys in formulas, for example <code>d1 * 10 + d5 * 5</code>. <code>x</code> is the current sensor value.</p>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 text-red-700 px-4 py-2 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError("")} className="font-bold px-2">×</button>
        </div>
      )}

      <div className="flex gap-4 mb-6">
        {["sensors", "relays", "rulebook"].map((item) => (
          <button
            key={item}
            onClick={() => setTab(item)}
            className={`${tabClass} ${tab === item ? "bg-green-500 text-white" : "bg-gray-200 text-gray-900"}`}
          >
            {item}
          </button>
        ))}
      </div>

      {tab === "sensors" && (
        <div className="space-y-4">
          <details open className="rounded-lg border border-slate-200 bg-slate-950 text-slate-100">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold marker:text-emerald-300">Formula guide</summary>
            <div className="grid gap-4 border-t border-slate-700 px-4 py-4 sm:grid-cols-2 xl:grid-cols-4">
              {formulaGuide.map((section) => (
                <section key={section.title}>
                  <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">{section.title}</h2>
                  <ul className="space-y-2">
                    {section.items.map(([code, description]) => (
                      <li key={code} className="text-xs leading-5 text-slate-300">
                        <code className="mr-1 rounded border border-slate-600 bg-slate-900 px-1.5 py-0.5 text-sky-300">{code}</code>
                        {description}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </details>
          <div className="bg-green-50 p-4 rounded overflow-hidden">
            <div className={sensorGridClass} style={sensorGridStyle}>
              <input placeholder="Key (d1)" value={newSensor.key} onChange={(event) => setNewSensor({ ...newSensor, key: event.target.value })} className={fieldClass} />
              <input placeholder="Label" value={newSensor.label} onChange={(event) => setNewSensor({ ...newSensor, label: event.target.value })} className={fieldClass} />
              <input placeholder="Factor" value={newSensor.factor} onChange={(event) => setNewSensor({ ...newSensor, factor: event.target.value })} className={fieldClass} />
              <input placeholder="Unit" value={newSensor.unit} onChange={(event) => setNewSensor({ ...newSensor, unit: event.target.value })} className={fieldClass} />
              <input placeholder="Group" value={newSensor.group_type} onChange={(event) => setNewSensor({ ...newSensor, group_type: event.target.value })} className={fieldClass} />
              <input placeholder="Formula (x/10)" value={newSensor.formula} onChange={(event) => setNewSensor({ ...newSensor, formula: event.target.value })} className={fieldClass} />
              <input placeholder="Min" value={newSensor.min_value} onChange={(event) => setNewSensor({ ...newSensor, min_value: event.target.value })} className={fieldClass} />
              <input placeholder="Max" value={newSensor.max_value} onChange={(event) => setNewSensor({ ...newSensor, max_value: event.target.value })} className={fieldClass} />
              <button onClick={saveSensor} className="h-10 bg-green-600 hover:bg-green-700 text-white px-2 rounded font-semibold">Add</button>
            </div>
          </div>

          {sensors.map((sensor) => (
            <div key={sensor.id} className="bg-white p-4 rounded shadow overflow-hidden">
              <div className={sensorGridClass} style={sensorGridStyle}>
                <input value={sensor.key} disabled placeholder="Key" className={disabledFieldClass} />
                <input
                  value={sensorField(sensor, "label")}
                  placeholder="Label"
                  onChange={(event) => setSensorField(sensor.id, "label", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "label")}
                  className={fieldClass}
                />
                <input
                  value={sensorField(sensor, "factor")}
                  placeholder="Factor"
                  onChange={(event) => setSensorField(sensor.id, "factor", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "factor")}
                  className={fieldClass}
                />
                <input
                  value={sensorField(sensor, "unit")}
                  placeholder="Unit"
                  onChange={(event) => setSensorField(sensor.id, "unit", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "unit")}
                  className={fieldClass}
                />
                <select
                  value={sensorField(sensor, "group_type", "general")}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSensorField(sensor.id, "group_type", value);
                    updateSensor({ ...sensor, group_type: value }, ["group_type"]);
                  }}
                  className={fieldClass}
                >
                  <option value="inlet">Inlet</option>
                  <option value="outlet">Outlet</option>
                  <option value="level">Water Level</option>
                  <option value="ambient">Ambient</option>
                  <option value="general">General</option>
                </select>
                <input
                  value={sensorField(sensor, "formula")}
                  placeholder="Formula (x/10)"
                  onChange={(event) => setSensorField(sensor.id, "formula", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "formula")}
                  className={fieldClass}
                />
                <input
                  value={sensorField(sensor, "min_value")}
                  placeholder="Min"
                  onChange={(event) => setSensorField(sensor.id, "min_value", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "min_value")}
                  className={fieldClass}
                />
                <input
                  value={sensorField(sensor, "max_value")}
                  placeholder="Max"
                  onChange={(event) => setSensorField(sensor.id, "max_value", event.target.value)}
                  onBlur={() => commitSensorField(sensor, "max_value")}
                  className={fieldClass}
                />
                <button onClick={() => deleteSensor(sensor.id)} title="Delete sensor" className="h-10 rounded border border-red-200 bg-red-50 text-red-600 font-bold hover:bg-red-100">X</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "relays" && (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded overflow-hidden">
            <div className={relayGridClass} style={relayGridStyle}>
              <input placeholder="Key (i1)" value={newRelay.key} onChange={(event) => setNewRelay({ ...newRelay, key: event.target.value })} className={fieldClass} />
              <input placeholder="Label" value={newRelay.label} onChange={(event) => setNewRelay({ ...newRelay, label: event.target.value })} className={fieldClass} />
              <select value={newRelay.mode} onChange={(event) => setNewRelay({ ...newRelay, mode: event.target.value })} className={fieldClass}>
                <option value="manual">Manual</option>
                <option value="auto">Auto</option>
                <option value="schedule">Schedule</option>
              </select>
              {newRelay.mode === "auto" ? renderTriggerFields(newRelay, (patch) => setNewRelay({ ...newRelay, ...patch })) : emptyRelayTrigger}
              {newRelay.mode !== "manual" ? renderScheduleFields(newRelay, (patch) => setNewRelay({ ...newRelay, ...patch })) : emptyRelayTiming}
              <button onClick={saveRelay} className="h-10 bg-green-600 hover:bg-green-700 text-white px-2 rounded font-semibold">Add</button>
            </div>
          </div>

          {relays.map((relay) => {
            const draft = relayDraft(relay);
            const mode = draft.mode === "timer" ? "schedule" : draft.mode || "manual";
            return (
              <div key={relay.id} className="bg-white p-4 rounded shadow overflow-hidden">
                <div className={relayGridClass} style={relayGridStyle}>
                  <input value={relay.key} disabled placeholder="Key" className={disabledFieldClass} />
                  <input
                    value={relayField(relay, "label")}
                    placeholder="Label"
                    onChange={(event) => setRelayField(relay.id, "label", event.target.value)}
                    onBlur={() => commitRelayField(relay, "label")}
                    className={fieldClass}
                  />
                  <select
                    value={mode}
                    onChange={(event) => updateRelay({ ...relay, mode: event.target.value }, ["mode"])}
                    className={fieldClass}
                  >
                    <option value="manual">Manual</option>
                    <option value="auto">Auto</option>
                    <option value="schedule">Schedule</option>
                  </select>
                  {mode === "auto"
                    ? renderTriggerFields(
                        draft,
                        (patch) => setEditingRelays((prev) => ({ ...prev, [relay.id]: { ...prev[relay.id], ...patch } })),
                        (field, valueOverride) => commitRelayField(relay, field, valueOverride)
                      )
                    : emptyRelayTrigger}
                  {mode !== "manual"
                    ? renderScheduleFields(
                        draft,
                        (patch) => setEditingRelays((prev) => ({ ...prev, [relay.id]: { ...prev[relay.id], ...patch } })),
                        (field, valueOverride) => commitRelayField(relay, field, valueOverride)
                      )
                    : emptyRelayTiming}
                  <button onClick={() => deleteRelay(relay.id)} title="Delete relay" className="h-10 rounded border border-red-200 bg-red-50 text-red-600 font-bold hover:bg-red-100">X</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "rulebook" && (
        <div className="max-w-3xl space-y-3 rounded-lg border border-slate-200 bg-white p-5">
          <div>
            <h2 className="text-base font-bold text-slate-900">Device Rule Book</h2>
            <p className="mt-1 text-sm text-slate-500">Record operating rules, sensor meanings, expected ranges, and formula references for every device using this product.</p>
          </div>
          <textarea value={ruleBook} onChange={(event) => setRuleBook(event.target.value)} rows={12} placeholder="Example: d1 = inlet concentration. AQI = round((d1 * 10 + d5 * 5) / 25, 1)." className="w-full rounded border border-slate-300 p-3 font-mono text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20" />
          <button type="button" disabled={savingRuleBook} onClick={saveRuleBook} className="h-10 rounded bg-green-600 px-4 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">{savingRuleBook ? "Saving..." : "Save Rule Book"}</button>
        </div>
      )}
    </div>
  );
}
